// Regras puras do Chat Interno (mockup "Tela Aeros Dark - Chat Interno e
// Modais"): título/avatar da conversa, prévia da última mensagem, filtros,
// hora relativa, agrupamento de bolhas e prazo de edição.
import { initialsOf, avatarColorFor } from "../Contacts/contactRules";
import { i18n } from "../../translate/i18n";

export { initialsOf, avatarColorFor };

export const isGroup = (chat) => chat?.isGroup === true || chat?.isGroup === "true";

export const otherUserOf = (chat, meId) =>
  (chat?.users || []).find((u) => Number(u.userId) !== Number(meId))?.user || null;

export const chatTitle = (chat, meId) =>
  isGroup(chat) ? chat.title || i18n.t("chatIndex.filters.groups") : otherUserOf(chat, meId)?.name || i18n.t("aeros.chat.user");

export const unreadOf = (chat, meId) =>
  Number((chat?.users || []).find((u) => Number(u.userId) === Number(meId))?.unreads || 0);

// "Você: …" / "Carlos: …" / "Áudio · 0:18"
export const lastPreview = (chat, meId) => {
  const m = chat?.lastMessage;
  if (!m) return "";
  if (typeof m === "string") return m;
  const who = Number(m.senderId) === Number(meId) ? i18n.t("aeros.chat.you") : (m.sender?.name || "").split(" ")[0];
  let body = "";
  if (m.isDeleted) body = i18n.t("aeros.chat.deleted");
  else if (m.mediaType === "audio") body = i18n.t("aeros.chat.audio");
  else if (m.mediaType === "image") body = i18n.t("aeros.chat.image");
  else if (m.mediaType === "video") body = i18n.t("aeros.chat.video");
  else if (m.mediaType && m.mediaType !== "text" && m.mediaName) body = `${i18n.t("aeros.chat.file")} · ${m.mediaName}`;
  else body = m.message || "";
  return who ? `${who}: ${body}` : body;
};

export const FILTERS = ["all", "unread", "groups"];

export const filterChats = (chats, filter, meId) =>
  (chats || []).filter((c) => {
    if (filter === "unread") return unreadOf(c, meId) > 0;
    if (filter === "groups") return isGroup(c);
    return true;
  });

export const filterCounts = (chats, meId) => ({
  all: (chats || []).length,
  unread: (chats || []).filter((c) => unreadOf(c, meId) > 0).length,
  groups: (chats || []).filter(isGroup).length,
});

export const matchesSearch = (chat, term, meId) => {
  const t = String(term || "").trim().toLowerCase();
  if (!t) return true;
  return chatTitle(chat, meId).toLowerCase().includes(t);
};

// "14:08" hoje · "Ontem" · "Seg" na semana · "dd/MM" depois
export const timeLabel = (date, now = new Date()) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, now)) return d.toLocaleTimeString(i18n.language?.startsWith("pt") ? "pt-BR" : i18n.language || "pt-BR", { hour: "2-digit", minute: "2-digit" });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return i18n.t("aeros.chat.yesterday");
  const diff = (now.getTime() - d.getTime()) / 86400000;
  if (diff < 7) {
    const dias = i18n.t("aeros.chat.weekdays").split(",");
    return dias[d.getDay()];
  }
  return d.toLocaleDateString(i18n.language?.startsWith("pt") ? "pt-BR" : i18n.language || "pt-BR", { day: "2-digit", month: "2-digit" });
};

export const dayLabel = (date, now = new Date()) => {
  const d = new Date(date);
  if (d.toDateString() === now.toDateString()) return "HOJE";
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "ONTEM";
  return d.toLocaleDateString("pt-BR");
};

// Mesma pessoa em até 5 min = continuação (sem avatar/nome de novo).
export const isContinuation = (prev, msg) => {
  if (!prev || !msg) return false;
  if (Number(prev.senderId) !== Number(msg.senderId)) return false;
  if (prev.isSystem || msg.isSystem) return false;
  const a = new Date(prev.createdAt).getTime();
  const b = new Date(msg.createdAt).getTime();
  return b - a < 5 * 60 * 1000 && new Date(a).toDateString() === new Date(b).toDateString();
};

export const EDIT_WINDOW_MIN = 10;
export const editMinutesLeft = (createdAt, now = new Date()) =>
  Math.max(0, Math.ceil((EDIT_WINDOW_MIN * 60 * 1000 - (now.getTime() - new Date(createdAt).getTime())) / 60000));

// Chat direto já existente entre mim e o usuário.
export const findDirectChat = (chats, meId, userId) =>
  (chats || []).find(
    (c) =>
      !isGroup(c) &&
      (c.users || []).length === 2 &&
      c.users.some((u) => Number(u.userId) === Number(meId)) &&
      c.users.some((u) => Number(u.userId) === Number(userId))
  ) || null;

export const onlineCount = (chat, onlineUsers) => {
  const on = new Set((onlineUsers || []).filter((u) => u.online).map((u) => Number(u.id)));
  return (chat?.users || []).filter((u) => on.has(Number(u.userId))).length;
};

export const isMuted = (chat, meId) =>
  Boolean((chat?.users || []).find((u) => Number(u.userId) === Number(meId))?.muted);

// [{ emoji, count, mine, names }] a partir das reações da mensagem.
export const groupReactions = (reactions, meId) => {
  const map = new Map();
  (reactions || []).forEach((r) => {
    const g = map.get(r.emoji) || { emoji: r.emoji, count: 0, mine: false, names: [] };
    g.count += 1;
    if (Number(r.userId) === Number(meId)) g.mine = true;
    if (r.user?.name) g.names.push(r.user.name.split(" ")[0]);
    map.set(r.emoji, g);
  });
  return Array.from(map.values());
};

export const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

// Token "@xxx" em edição no fim do texto (para o autocomplete de menção).
export const mentionQuery = (text) => {
  const m = /(?:^|\s)@([^\s@]*)$/.exec(String(text || ""));
  return m ? m[1] : null;
};

// Substitui o token "@xxx" final por "@Primeiro ".
export const applyMention = (text, name) =>
  String(text || "").replace(/@([^\s@]*)$/, `@${String(name || "").split(" ")[0]} `);

export const typingLabel = (typingUsers) => {
  const names = Object.values(typingUsers || {}).map((n) => String(n).split(" ")[0]).filter(Boolean);
  if (!names.length) return "";
  if (names.length === 1) return i18n.t("aeros.chat.typingOne", { name: names[0] });
  return i18n.t("aeros.chat.typingMany", { names: names.slice(0, 2).join(i18n.t("aeros.chat.and")) });
};
